import { useEffect, useState } from "react";
import moment from "moment";
import PartyCardDetail from "./partyCardDetail";
import { toastError } from "../../utils";
import PaymentModal from "../Modal/payment";
import { Link, useParams } from "react-router-dom";
import TransactionModal from "../Modal/Transaction";
import {
  getPartyDetails,
  getPurchaseReturnById,
  getSaleReturnById,
  getSinglePurchase,
  getSingleSale,
  updateParty,
} from "../../services/services";
import headerLayoutWrapper from "../../hoc/headerLayoutWrapper";
import DataTable from "react-data-table-component";
import { ArrowLeft } from "lucide-react";
import Pagination from "../../components/Pagination";
import { toast } from "react-toastify";
import Loading from "../../components/loading";

const PartyDetails = () => {
  const { id } = useParams();
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [partyData, setPartyData] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);

  const [editSection, setEditSection] = useState(null);
  // "basic" | "tax" | "address" | null

  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    state: "",
    partyType: "",
    gstin: "",
    gstType: "",
    billingAddress: [""],
    shippingAddress: [""],
    sameAsBilling: false,
  });

  const [defaultPaymentType, setDefaultPaymentType] = useState("payment-in");

  const getDefaultPaymentType = (balance) => {
    if (balance > 0) return "payment-in"; // party owes you → you receive
    if (balance < 0) return "payment-out"; // you owe party → you pay
    return "payment-in"; // default
  };

  const fetchPartyData = async (page = 1, limit) => {
    setLoading(true);
    try {
      const payload = {
        page,
        limit,
      };

      const response = await getPartyDetails(id, payload);
      if (response.data.status) {
        const {
          party: fullParty,
          balance,
          balanceType,
          ledger = [],
          pagination,
        } = response.data.response;

        setPartyData({ ...fullParty, balance, balanceType, ledger });

        if (pagination) {
          setTotalPages(pagination.totalPages || 1);
        }
      }
    } catch (error) {
      console.error("Error:", error);
      toastError("Failed to load party data");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = (response = null) => {
    if (response?.data?.party) {
      setPartyData((prev) => ({
        ...prev,
        credit_balance: response.data.party.credit_balance,
        // balance: response.data.party.balance || prev.balance,
      }));
    }
    setCurrentPage(1);
    fetchPartyData(1, limit);
    setShowPaymentModal(false);
    setShowTransactionModal(false);
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#0b2b6d",
        minHeight: "44px",
      },
    },
    headCells: {
      style: {
        color: "#ffffff",
        fontSize: "13px",
        fontWeight: "600",
        textTransform: "uppercase",
        // borderRight: "1px solid rgba(255,255,255,0.15)",
      },
    },
    rows: {
      style: {
        minHeight: "48px",
      },
    },
  };

  useEffect(() => {
    fetchPartyData(currentPage, limit);
  }, [id, currentPage, limit]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    // fetchPartyData(newPage, limit);
  };
  const handleLedgerClick = async (row) => {
    const ledgerWithParty = {
      ...row,
      party: partyData,
    };

    // Normal ledger types
    if (
      row.referenceType !== "sale" &&
      row.referenceType !== "purchase" &&
      row.referenceType !== "saleReturn" &&
      row.referenceType !== "purchaseReturn"
    ) {
      setSelectedLedger(ledgerWithParty);
      return;
    }

    try {
      // ================= SALE =================
      if (row.referenceType === "sale") {
        const res = await getSingleSale(row.referenceId);

        if (res.data.status) {
          const singleSale = res.data.response.singleSale;

          setSelectedLedger({
            ...singleSale,
            party: partyData,
            date: singleSale.saleDate,
            referenceType: "sale",
            referenceId: singleSale._id,
            invoiceNumber: singleSale.invoiceNumber,
            totalAmount: singleSale.totalAmount,
            receivedAmount: singleSale.receivedAmount,
            dueAmount: singleSale.dueAmount,
            items: singleSale.items,
            services: singleSale.services,
          });
        }
      }

      // ================= PURCHASE =================
      if (row.referenceType === "purchase") {
        const res = await getSinglePurchase(row.referenceId);

        if (res.data.status) {
          const singlePurchase = res.data.response.singlePurchase;

          setSelectedLedger({
            ...singlePurchase,
            party: partyData,
            date: singlePurchase.purchaseDate,
            referenceType: "purchase",
            referenceId: singlePurchase._id,
            invoiceNumber: singlePurchase.invoiceNumber,
            totalAmount: singlePurchase.totalAmount,
            paidAmount: singlePurchase.paidAmount,
            dueAmount: singlePurchase.dueAmount,
            items: singlePurchase.items,
            services: singlePurchase.services,
          });
        }
      }
      // ================= SALE RETURN =================
      if (row.referenceType === "saleReturn") {
        const res = await getSaleReturnById(row.referenceId);

        if (res.data.success && res.data.data) {
          const returnData = res.data.data;

          setSelectedLedger({
            party: returnData.party,
            date: returnData.date,
            referenceType: "saleReturn",
            referenceId: returnData._id,
            invoiceNumber: returnData.invoiceNumber,

            // Calculate return total
            totalAmount: returnData.items.reduce(
              (sum, item) => sum + (item.totalReturnAmount || 0),
              0,
            ),

            receivedAmount: 0,
            dueAmount: 0,

            items: returnData.items,
            services: returnData.services || [],

            reason: returnData.reason,
            returnQuantity: returnData.quantity,
          });
        }
      }
      // ================= PURCHASE RETURN =================
      if (row.referenceType === "purchaseReturn") {
        const res = await getPurchaseReturnById(row.referenceId);

        if (res.data.success && res.data.data) {
          const returnData = res.data.data;

          setSelectedLedger({
            party: returnData.party,
            date: returnData.date,
            referenceType: "purchaseReturn",
            referenceId: returnData._id,
            invoiceNumber: returnData.invoiceNumber,

            // Calculate return total
            totalAmount: returnData.items.reduce(
              (sum, item) => sum + (item.totalReturnAmount || 0),
              0,
            ),

            paidAmount: 0,
            dueAmount: 0,

            items: returnData.items,
            services: returnData.services || [],

            reason: returnData.reason,
            returnQuantity: returnData.quantity,
            batchCode: returnData.batchCode,
          });
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load transaction details");
    }
  };

  const handleDeleteSuccess = () => {
    fetchPartyData(currentPage, limit);
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setCurrentPage(1);
    fetchPartyData(1, newLimit);
  };

  const startEdit = (section) => {
    setEditSection(section);
    const billing = Array.isArray(partyData.billingAddress)
      ? partyData.billingAddress.map((a) => a.address)
      : partyData.billingAddress
        ? [partyData.billingAddress]
        : [""];

    const shipping = Array.isArray(partyData.shippingAddress)
      ? partyData.shippingAddress.map((a) => a.address)
      : partyData.shippingAddress
        ? [partyData.shippingAddress]
        : [""];
    setFormData({
      name: partyData.name || "",
      mobile: partyData.mobile || "",
      email: partyData.email || "",
      state: partyData.state || "",
      partyType: partyData.partyType || "",
      gstin: partyData.gstin || "",
      gstType: partyData.gstType || "",
      billingAddress: billing,
      shippingAddress: shipping,
      sameAsBilling: false,
    });
  };

  const handleSave = async (section) => {
    try {
      const payload = {
        name: partyData.name,
      };

      if (section === "basic") {
        if (!/^[6-9]\d{9}$/.test(formData.mobile)) {
          toast.error("Enter valid 10 digit mobile number");
          return;
        }

        if (
          formData.email &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ) {
          toast.error("Enter valid email address");
          return;
        }
        payload.name = formData.name;
        payload.mobile = formData.mobile;
        payload.email = formData.email ? formData.email.toLowerCase() : "";
        payload.state = formData.state;
        payload.partyType = formData.partyType;
      }

      if (section === "tax") {
        const gstRegex =
          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

        const gstin = formData.gstin ? formData.gstin.toUpperCase() : "";

        if (gstin && !gstRegex.test(gstin)) {
          toast.error("Enter valid GST number");
          return;
        }

        payload.gstin = gstin;
        payload.gstType = formData.gstType;
      }

      if (section === "address") {
        payload.billingAddress = formData.billingAddress;
        payload.shippingAddress = formData.sameAsBilling
          ? formData.billingAddress
          : formData.shippingAddress;
      }

      await updateParty(id, payload);

      toast.success("Party updated");

      setPartyData((prev) => ({
        ...prev,
        ...payload,
      }));
      await fetchPartyData(currentPage, limit);
      setEditSection(null);
    } catch (err) {
      toast.error("Failed to update party");
    }
  };

  if (!partyData) return <Loading />;

  const {
    name,
    mobile,
    email,
    gstin,
    billingAddress,
    shippingAddress,
    state,
    gstType = "Unregistered",
    partyType,
    openingBalance = 0,
    openingBalanceType,
    date,
    createdAt,
    updatedAt,
    balance,
    balanceType,
    credit_balance,
  } = partyData;

  const ledgerColumns = [
    {
      name: "Type",
      cell: (row) => {
        const isDebit = (row.debit || 0) > 0;

        return (
          <div className="d-flex flex-column text-nowrap my-2">
            <div
              className={
                isDebit
                  ? "text-success fw-semibold" // money coming (you'll get)
                  : "text-danger fw-semibold" // money going (you'll give)
              }
            >
              {row.referenceType.toUpperCase()}
            </div>
          </div>
        );
      },
      grow: 2,
    },
    ,
    {
      name: "Total",
      cell: (row) =>
        row.referenceType === "opening" ? (
          <span className="text-nowrap">
            ₹ {Math.abs(partyData?.openingBalance || 0).toFixed(2)}
          </span>
        ) : (
          <span className="text-nowrap">
            ₹ {Math.abs(row.totalAmount).toFixed(2)}
          </span>
        ),
    },
    {
      name: "Paid / Received",
      width: "10rem",
      right: true,
      cell: (row) => {
        const amount = row.paidAmount ?? row.receivedAmount ?? 0;

        return (
          <span className="text-nowrap">₹ {Math.abs(amount).toFixed(2)}</span>
        );
      },
    },
    // {
    //   name: "Due",
    //   right: true,
    //   cell: (row) =>
    //     row.referenceType === "opening" ? (
    //       "-"
    //     ) : (
    //       <span className="text-danger text-nowrap">
    //         ₹ {Math.abs(row?.dueAmount).toFixed(2)}
    //       </span>
    //     ),
    // },
    {
      name: "Date",
      right: true,
      selector: (row) => moment(row.date).format("DD MMM, YY"),
    },
  ];
  const getCreditLabel = () => "Advance / Credit";

  const balanceValue = Number(balance || 0);

  const balanceLabel =
    balanceValue > 0
      ? "You'll Get"
      : balanceValue < 0
        ? "You'll Give"
        : "Settled";

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div id="main">
          <div className="mb-3 d-flex gap-2 align-items-center">
            <Link to="/party" style={{ fontSize: "12px" }}>
              <ArrowLeft className="mb-0" />
            </Link>
            <h2 className="custom-title mb-0">Party Details</h2>
          </div>
          <div className="row g-3">
            <div className="col-12 col-md-5 col-lg-4">
              <div className="card shadow-sm pb-0">
                <div className="card-body pb-0">
                  <div className="row g-1">
                    <div className="row g-3">
                      <h6 className="small fw-semibold mb-2">
                        Financial Summary
                      </h6>
                      {/* Opening Balance */}

                      <div className="col-12 col-lg-6 mr-2">
                        <div className="card shadow-sm ">
                          <div className="card-body p-3">
                            <h6 className="text-muted small text-nowrap">
                              Opening Balance
                            </h6>
                            <div className="fw-medium my-2">
                              ₹ {Math.abs(openingBalance).toFixed(2)}
                            </div>
                            {/* {openingBalanceType && (
                                  <small
                                    className={`small ${
                                      openingBalanceType === "toReceive"
                                        ? "text-success"
                                        : "text-danger"
                                    }`}
                                  >
                                    {openingBalanceType === "toReceive"
                                      ? "You'll Get"
                                      : "You'll Give"}
                                  </small>
                                )} */}
                          </div>
                        </div>
                      </div>

                      {/* Credit / Advance Balance */}
                      <div className="col-12 col-lg-6">
                        <div className="card shadow-sm">
                          <div className="card-body p-3">
                            <h6 className="text-muted small text-nowrap">
                              {getCreditLabel(partyType)}
                            </h6>

                            <div className="fw-medium my-2">
                              ₹ {Math.abs(credit_balance || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary Section */}
                    <div className="col-12">
                      <div className="">
                        {/* Current Balance - Highlighted */}

                        <div
                          className={`card shadow-sm ${
                            balanceValue < 0
                              ? "bg-danger bg-opacity-10"
                              : balanceValue > 0
                                ? "bg-success bg-opacity-10"
                                : "bg-light"
                          }`}
                        >
                          <div className="card-body text-center p-3">
                            <h6>Current Balance</h6>

                            <div
                              className={`fs-5 fw-bold my-2 ${
                                balanceValue < 0
                                  ? "text-danger"
                                  : balanceValue > 0
                                    ? "text-success"
                                    : "text-dark"
                              }`}
                            >
                              ₹ {Math.abs(balanceValue).toFixed(2)}
                            </div>

                            <small className="small">{balanceLabel}</small>
                          </div>
                        </div>
                        <div className="d-flex justify-content-end mt-4">
                          <button
                            className="btn btn-custom d-flex align-items-center"
                            style={{
                              height: "36px",
                              minWidth: "100px",
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#0b2b6d",
                            }}
                            onClick={() => {
                              setDefaultPaymentType(
                                getDefaultPaymentType(partyData?.balance),
                              );
                              setShowPaymentModal(true);
                            }}
                          >
                            Payments
                          </button>
                        </div>
                        <hr style={{ borderTop: "2px dashed #999" }} />
                      </div>
                    </div>

                    {/* Basic Information Section */}

                    <div className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="small fw-semibold">Basic Information</h6>

                        {editSection === "basic" ? (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-success text-success bg-transparent"
                              onClick={() => handleSave("basic")}
                            >
                              Save
                            </button>
                            {/* <button
                              className="btn btn-sm text-success border-0 bg-transparent"
                              onClick={() => handleSave("basic")}
                              title="Save"
                            >
                              <i className="bi bi-check-lg fs-5"></i>
                            </button> */}
                            <button
                              className="btn btn-sm text-danger border-0 bg-transparent"
                              onClick={() => setEditSection(null)}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg fs-5"></i>
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm p-0 text-primary border-0 bg-transparent"
                            onClick={() => startEdit("basic")}
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      <div className="list-group list-group-flush small">
                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">Name</small>
                            </div>
                            <div className="col-8">
                              {editSection === "basic" ? (
                                <input
                                  className="form-control form-control-sm"
                                  value={formData.name || ""}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      name: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                <span className="fw-medium">{name || "-"}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">Mobile</small>
                            </div>
                            <div className="col-8">
                              {editSection === "basic" ? (
                                <input
                                  type="tel"
                                  className="form-control form-control-sm"
                                  value={formData.mobile || ""}
                                  maxLength={10}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    ); // remove non-digits

                                    if (value.length <= 10) {
                                      setFormData({
                                        ...formData,
                                        mobile: value,
                                      });
                                    }
                                  }}
                                />
                              ) : (
                                <span className="fw-medium">
                                  {mobile || "-"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">Email</small>
                            </div>
                            <div className="col-8">
                              {editSection === "basic" ? (
                                <input
                                  className="form-control form-control-sm"
                                  value={formData.email || ""}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      email: e.target.value.toLowerCase(),
                                    })
                                  }
                                />
                              ) : (
                                <span className="fw-medium">
                                  {email || "-"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">Party Type</small>
                            </div>
                            <div className="col-8">
                              {partyType ? (
                                <span
                                  className={`badge ${
                                    partyType === "customer"
                                      ? "bg-info"
                                      : "bg-secondary"
                                  }`}
                                >
                                  {partyType.charAt(0).toUpperCase() +
                                    partyType.slice(1)}
                                </span>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">Created On</small>
                            </div>
                            <div className="col-8">
                              <span className="fw-medium">
                                {date
                                  ? moment(date).format("DD MMM, YYYY")
                                  : "-"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr style={{ borderTop: "2px dashed #999" }} />

                    {/* GST & Party Details Section */}

                    <div className="">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="small fw-semibold">
                          Tax & Registration
                        </h6>

                        {editSection === "tax" ? (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-success text-success bg-transparent"
                              onClick={() => handleSave("tax")}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm text-danger border-0 bg-transparent"
                              onClick={() => setEditSection(null)}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg fs-5"></i>
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm p-0 text-primary border-0 bg-transparent"
                            onClick={() => startEdit("tax")}
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      <div className="list-group list-group-flush small">
                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">GSTIN</small>
                            </div>
                            <div className="col-8">
                              {editSection === "tax" ? (
                                <input
                                  className="form-control form-control-sm"
                                  value={formData.gstin || ""}
                                  maxLength={15}
                                  style={{ textTransform: "uppercase" }}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      gstin: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                <span className="fw-medium">
                                  {gstin || "-"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="px-0 py-2 border-0">
                          <div className="row align-items-center">
                            <div className="col-4">
                              <small className="text-muted">GST Type</small>
                            </div>
                            <div className="col-8">
                              {editSection === "tax" ? (
                                <select
                                  className="form-select form-select-sm"
                                  value={formData.gstType || ""}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      gstType: e.target.value,
                                    })
                                  }
                                >
                                  <option value="Unregistered">
                                    Unregistered
                                  </option>
                                  <option value="Registered">Registered</option>
                                  <option value="Composition">
                                    Composition
                                  </option>
                                </select>
                              ) : (
                                <span>{gstType || "-"}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr style={{ borderTop: "2px dashed #999" }} />

                    {/* Address Section */}
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <h6 className="small fw-semibold mb-3">
                          Address Details
                        </h6>

                        {editSection === "address" ? (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-success text-success bg-transparent"
                              onClick={() => handleSave("address")}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-sm text-danger border-0 bg-transparent"
                              onClick={() => setEditSection(null)}
                              title="Cancel"
                            >
                              <i className="bi bi-x-lg fs-5"></i>
                            </button>
                          </div>
                        ) : (
                          <button
                            className="btn btn-sm p-0 text-primary border-0 bg-transparent"
                            onClick={() => startEdit("address")}
                          >
                            Edit
                          </button>
                        )}
                      </div>

                      {/* ================= BILLING ADDRESS ================= */}
                      <div className="col-12">
                        <div className="card shadow-sm">
                          <div className="card-body p-3">
                            <h6 className="text-muted small mb-2">
                              Billing Address
                            </h6>

                            {/* EDIT MODE */}
                            {editSection === "address" ? (
                              formData.billingAddress.map((addr, index) => (
                                <div key={index} className="d-flex gap-2 mb-2">
                                  <textarea
                                    className="form-control form-control-sm"
                                    rows={2}
                                    value={addr}
                                    onChange={(e) => {
                                      const newArr = [
                                        ...formData.billingAddress,
                                      ];
                                      newArr[index] = e.target.value;
                                      setFormData({
                                        ...formData,
                                        billingAddress: newArr,
                                      });
                                    }}
                                  />

                                  {formData.billingAddress.length > 1 && (
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => {
                                        const newArr =
                                          formData.billingAddress.filter(
                                            (_, i) => i !== index,
                                          );
                                        setFormData({
                                          ...formData,
                                          billingAddress: newArr,
                                        });
                                      }}
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : billingAddress?.length > 0 ? (
                              <ul className="ps-3 small mb-0">
                                {billingAddress.map((a, i) => (
                                  <li key={a._id || i}>{a.address}</li>
                                ))}
                              </ul>
                            ) : null}

                            {/* Add button only in edit mode */}
                            {editSection === "address" && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() =>
                                  setFormData({
                                    ...formData,
                                    billingAddress: [
                                      ...formData.billingAddress,
                                      "",
                                    ],
                                  })
                                }
                              >
                                + Add Billing Address
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* ================= SHIPPING ADDRESS ================= */}
                      <div className="col-12">
                        <div className="card shadow-sm">
                          <div className="card-body p-3">
                            <h6 className="text-muted small mb-2">
                              Shipping Address
                            </h6>

                            {/* SAME AS BILLING TOGGLE */}
                            {/* {editSection === "address" && (
                              <div className="form-check mb-2">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={formData.sameAsBilling}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      sameAsBilling: e.target.checked,
                                      shippingAddress: e.target.checked
                                        ? [...formData.billingAddress]
                                        : [""],
                                    })
                                  }
                                />
                                <label className="form-check-label small">
                                  Same as Billing Address
                                </label>
                              </div>
                            )} */}

                            {/* ================= EDIT MODE ================= */}
                            {editSection === "address" ? (
                              !formData.sameAsBilling &&
                              formData.shippingAddress.map((addr, index) => (
                                <div key={index} className="d-flex gap-2 mb-2">
                                  <textarea
                                    className="form-control form-control-sm"
                                    rows={2}
                                    value={addr}
                                    onChange={(e) => {
                                      const newArr = [
                                        ...formData.shippingAddress,
                                      ];
                                      newArr[index] = e.target.value;
                                      setFormData({
                                        ...formData,
                                        shippingAddress: newArr,
                                      });
                                    }}
                                  />

                                  {formData.shippingAddress.length > 1 && (
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => {
                                        const newArr =
                                          formData.shippingAddress.filter(
                                            (_, i) => i !== index,
                                          );
                                        setFormData({
                                          ...formData,
                                          shippingAddress: newArr,
                                        });
                                      }}
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>
                              ))
                            ) : shippingAddress?.length > 0 ? (
                              <ul className="ps-3 small mb-0">
                                {shippingAddress.map((a, i) => (
                                  <li key={a._id || i}>{a.address}</li>
                                ))}
                              </ul>
                            ) : null}

                            {/* ADD BUTTON */}
                            {editSection === "address" &&
                              !formData.sameAsBilling && (
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() =>
                                    setFormData({
                                      ...formData,
                                      shippingAddress: [
                                        ...formData.shippingAddress,
                                        "",
                                      ],
                                    })
                                  }
                                >
                                  + Add Shipping Address
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12  col-md-7 col-lg-8">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <p className="mb-0">Transactions</p>

                <p
                  className={`fw-semibold mb-0 ${
                    balanceValue < 0
                      ? "text-danger"
                      : balanceValue > 0
                        ? "text-success"
                        : "text-dark"
                  }`}
                >
                  ₹ {Math.abs(balanceValue).toFixed(2)} ({balanceLabel})
                </p>
              </div>
              <div className="rounded">
                <DataTable
                  columns={ledgerColumns}
                  data={partyData?.ledger || []}
                  highlightOnHover
                  customStyles={customStyles}
                  pointerOnHover
                  onRowClicked={handleLedgerClick}
                  noDataComponent={
                    <div className="p-4 text-center text-muted">
                      <i className="bi bi-inbox display-6"></i>
                      <p className="mb-0">No transactions available.</p>
                    </div>
                  }
                  pagination={false}
                />
              </div>

              <div className="mt-2">
                <Pagination
                  page={currentPage}
                  totalPages={totalPages}
                  limit={limit}
                  onPageChange={handlePageChange}
                  onLimitChange={handleLimitChange}
                />
              </div>
            </div>
          </div>

          {selectedLedger && selectedLedger.referenceType !== "opening" && (
            <>
              <div
                className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-25"
                style={{ zIndex: 1049 }}
                onClick={() => setSelectedLedger(null)}
              />
              <PartyCardDetail
                ledgerEntry={selectedLedger}
                onClose={() => setSelectedLedger(null)}
                showActions={true}
                invoiceNumber={selectedLedger?.invoiceNumber}
                onSettlementSuccess={(updatedParty) => {
                  setPartyData((prev) => ({
                    ...prev,
                    credit_balance: updatedParty.credit_balance,
                  }));
                  fetchPartyData(currentPage, limit);
                }}
                onDeleteSuccess={handleDeleteSuccess}
              />
            </>
          )}

          <div className="position-fixed bottom-0 d-flex justify-content-center">
            {/* <button
          className="btn rounded-circle d-flex justify-content-center align-items-center"
          style={{
            width: "36px",
            height: "36px",
            fontWeight: "500",
            background: "#0b2b6d",
            color: "#fff",
            fontSize: "30px !important",
            padding: 0,
            lineHeight: 1,
          }}
          onClick={() => setShowTransactionModal(true)}
        >
          +
        </button>
        <button
          className="btn btn-custom d-flex justify-content-center align-items-center"
          style={{
            height: "36px",
            minWidth: "100px",
            fontSize: "14px",
            fontWeight: "500",
          }}
          onClick={() => navigate("/sales/create")}
        >
          Add Sale
        </button> */}
          </div>
        </div>
      )}

      {showPaymentModal && (
        <PaymentModal
          party={partyData}
          paymentType={defaultPaymentType}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
      {/* {showTransactionModal && (
        <TransactionModal
          show={showTransactionModal}
          onHide={() => setShowTransactionModal(false)}
          party={partyData}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )} */}
    </>
  );
};

export default headerLayoutWrapper(PartyDetails);
